import React, {useEffect, useState} from "react"
import {createPortal} from "react-dom"
// MUI
import {Box, Button, Typography} from "@mui/material"
import {AutoAwesomeRounded} from "@mui/icons-material"
// Translation
import {useTranslation} from "react-i18next"
// Misc
import {packagingTokens as tk} from "@/theme/packagingTokens"
import {PackagingStep} from "@/constants/packagingSteps"

const CARD_W = 300

// Spotlight for the "getting started" steps: dims the page with an opening
// around the action to perform and shows a Tour-style card (description +
// Close only). The whole overlay is pointer-events: none so the user can —
// and should — click the highlighted action right through it.
export default function StepSpotlight(props: {step: PackagingStep | null; onClose: () => void}) {

    const {step, onClose} = props
    const {t} = useTranslation(["common"])
    const [rect, setRect] = useState<DOMRect | null>(null)

    // The target section renders asynchronously after navigation, and its data
    // loads even later — a single measurement leaves the opening misplaced once
    // the layout settles. Keep re-measuring the whole time the spotlight is
    // open (cheap: one querySelector every 300ms; state only updates when the
    // rect actually moved, and the .25s CSS transition glides it into place).
    useEffect(() => {
        if (!step) { setRect(null); return }
        const measure = () => {
            const el = document.querySelector(`[data-step-target="${step.target}"]`)
            if (!el) return
            const r = el.getBoundingClientRect()
            setRect((prev) =>
                prev &&
                Math.abs(prev.top - r.top) < 1 && Math.abs(prev.left - r.left) < 1 &&
                Math.abs(prev.width - r.width) < 1 && Math.abs(prev.height - r.height) < 1
                    ? prev : r
            )
        }
        measure()
        const interval = setInterval(measure, 300)
        window.addEventListener("resize", measure)
        window.addEventListener("scroll", measure, true)
        return () => {
            clearInterval(interval)
            window.removeEventListener("resize", measure)
            window.removeEventListener("scroll", measure, true)
        }
    }, [step])

    if (!step) return null

    // Position in document coordinates (the app lives in a content-sized
    // iframe, so `fixed` would span the whole — possibly very tall — frame).
    // The body's rendered width is the only trustworthy viewport inside the
    // content-sized iframe — both innerWidth and documentElement.clientWidth
    // can exceed it and let the card clip off the right edge.
    const vw = document.body.clientWidth || document.documentElement.clientWidth
    const top = (rect?.top ?? 200) + window.scrollY
    const left = (rect?.left ?? vw / 2 - 60) + window.scrollX
    const pad = 6
    const cardTop = top + (rect?.height ?? 0) + pad * 2 + 10
    const cardLeft = Math.max(12, Math.min(left, vw - CARD_W - 24))

    // Portal to <body>: the layout's content column animates with a transform,
    // which would otherwise become the containing block and shift the overlay
    // by the sidebar/padding offset (card clipping off the right edge).
    return createPortal(
        <Box sx={{position: "absolute", inset: 0, zIndex: 1400, pointerEvents: "none"}} data-testid={"step-spotlight"}>
            {/* Dimming with an opening around the target: a SINGLE layer with
                the hole cut out via clip-path — the outer rectangle and the
                inner one are wound in opposite directions, so the nonzero
                fill rule turns the inner one into a hole. One element means
                no panel junctions, hence no hairline seams on the right and
                bottom of the opening (which the previous four-panel approach
                showed on scaled displays). Not the giant-box-shadow trick
                either: Chromium clamps huge spreads once the overlay is
                portaled to <body>. Clicks pass through. */}
            {(() => {
                const holeTop = Math.floor(top - pad)
                const holeLeft = Math.floor(left - pad)
                const holeW = Math.ceil(left + (rect?.width ?? 120) + pad) - holeLeft
                const holeH = Math.ceil(top + (rect?.height ?? 40) + pad) - holeTop
                const x1 = holeLeft + holeW
                const y1 = holeTop + holeH
                // Rounded hole, same radius as the white ring: a square hole
                // lets undimmed notches peek past the ring's rounded corners
                // and reads as a broken border. Each corner is a small arc
                // (7 points) — the polygon keeps a constant vertex count, so
                // the .25s clip-path transition still interpolates smoothly.
                const R = 10 // = tk.radius.md, the ring's border-radius
                const pt = (x: number, y: number) => `${x.toFixed(1)}px ${y.toFixed(1)}px`
                const arc = (cx: number, cy: number, from: number, to: number) => {
                    const pts: string[] = []
                    for (let i = 0; i <= 6; i++) {
                        const a = ((from + ((to - from) / 6) * i) * Math.PI) / 180
                        pts.push(pt(cx + R * Math.cos(a), cy + R * Math.sin(a)))
                    }
                    return pts
                }
                // Counter-clockwise (opposite of the outer loop) so the
                // nonzero fill rule turns the inner loop into a hole.
                const hole = [
                    ...arc(holeLeft + R, holeTop + R, 270, 180),
                    ...arc(holeLeft + R, y1 - R, 180, 90),
                    ...arc(x1 - R, y1 - R, 90, 0),
                    ...arc(x1 - R, holeTop + R, 0, -90),
                ]
                const clip = `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${hole.join(", ")}, ${hole[0]})`
                return (
                    <>
                        <Box sx={{position: "absolute", inset: 0, backgroundColor: "rgba(17, 19, 24, 0.55)", clipPath: clip, transition: "clip-path .25s ease"}} />
                        {/* white ring emphasising the opening — sits exactly on
                            the hole edge so ring and dim meet with no gap */}
                        <Box sx={{position: "absolute", top: holeTop, left: holeLeft, width: holeW, height: holeH, borderRadius: tk.radius.md, border: `2px solid ${tk.color.surface}`, transition: "all .25s ease"}} />
                    </>
                )
            })()}

            {/* Tour-style card — the only interactive part of the overlay. */}
            <Box
                sx={{
                    position: "absolute", top: cardTop, left: cardLeft, width: CARD_W,
                    backgroundColor: tk.color.surface, borderRadius: tk.radius.card,
                    boxShadow: tk.shadow.lg ?? "0 12px 32px rgba(0,0,0,.25)",
                    p: 2, pointerEvents: "auto",
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.75}}>
                    <AutoAwesomeRounded sx={{fontSize: 16, color: tk.color.green}} />
                    <Typography sx={{fontSize: "14px", fontWeight: 700}}>{t(`step_${step.id}`)}</Typography>
                </Box>
                <Typography sx={{fontSize: "13px", color: tk.color.textSecondary, mb: 1.5}}>
                    {t(`step_${step.id}_desc`)}
                </Typography>
                <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                    <Button
                        size={"small"} onClick={onClose}
                        sx={{textTransform: "none", fontWeight: 600, fontSize: "12.5px", borderRadius: tk.radius.sm, color: tk.color.textSecondary}}
                    >
                        {t("close")}
                    </Button>
                </Box>
            </Box>
        </Box>,
        document.body,
    )
}
